import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Flex from 'components/ui/Flex';
import ItemCountDisplay from 'components/ui/ItemCountDisplay';
import Table, { TableCell, TableRow } from 'components/ui/Table';
import Text from 'components/ui/Text';
import ReturnSummaryCell from 'components/ui/ReturnSummaryCell';
import useBreakPoints from 'hooks/useBreakPoints';
import { ReturnOrderHeader } from 'returnCenter-api';
import { styled } from 'styled-components';

type OrderResultsTableProps = {
    orders: ReturnOrderHeader[];
    onOrderSelect: (order: ReturnOrderHeader) => void;
    isOrderSelectable?: (order: ReturnOrderHeader) => boolean;
};

const ORDER_TABLE_COLS = [
    'orderNumber',
    'returnRegistered',
    'customerCode',
    'orderType',
    'packageLabel',
    'orderDateTime',
    'returnSummary',
] as const;

const OrderResultsTable = ({ orders, onOrderSelect, isOrderSelectable }: OrderResultsTableProps) => {
    const { t } = useTranslation();
    const bp = useBreakPoints();
    const compact = bp.width < 860;

    const headerLabels = [
        t('orderNumber'),
        t('returnRegistered'),
        t('customerCode'),
        t('orderType'),
        t('goodsMarking'),
        t('orderDateTime'),
        t('returnSummary', 'Return Summary'),
    ];

    const getCellValue = (column: string, order: ReturnOrderHeader) => {
        switch (column) {
            case 'orderNumber':
                return <Text>{order.orderNumber ?? t('missingInfo')}</Text>;
            case 'customerCode':
                return <Text>{order.customerCode ?? t('missingInfo')}</Text>;
            case 'orderType':
                return <Text>{order.orderType ?? t('missingInfo')}</Text>;
            case 'packageLabel':
                return <Text>{order.packageLabel}</Text>;
            case 'orderDateTime':
                return <Text>{order.orderDate?.str ?? t('missingInfo')}</Text>;
            case 'returnSummary':
                return <ReturnSummaryCell returnSummaries={order.returnSummaries} />;
            default:
                return null;
        }
    };

    const rows = useMemo(() => {
        return orders.map(
            (order): TableRow => {
                const cells: TableCell[] = ORDER_TABLE_COLS.map((col) => {
                    if (col === 'returnRegistered') {
                        return {
                            el: <Text>{order.hasReturnSlip ? t('Yes') : t('No')}</Text>,
                        };
                    }
                    return {
                        el: getCellValue(col, order),
                    };
                });

                const selectable = isOrderSelectable ? isOrderSelectable(order) : true;

                return {
                    onClick: selectable ? () => onOrderSelect(order) : undefined,
                    faded: !selectable,
                    cells,
                };
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, onOrderSelect, isOrderSelectable, t]);

    return (
        <Wrapper column gap="1rem" $grow={1}>
            <ItemCountDisplay title={t('orders')} count={orders.length} countLabel={t('returns')} />
            <Table
                className="orders-table f3-300"
                bgColor="fillWhite"
                cols={headerLabels.length}
                headers={headerLabels}
                rows={rows}
                compact={compact}
                arrow
                arrowLabel={t('createReturn')}
            />
        </Wrapper>
    );
};

export default OrderResultsTable;

const Wrapper = styled(Flex)`
    width: 100%;
`;
